import React from "react";
import { Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

/**
 * Master v5 Prompt 30C Phase 2 — `table` input type editor. Value is a
 * structured `{rows: [[cellText, ...], ...]}` — real row/column
 * structure is preserved, never collapsed into an HTML string.
 */
export function TableInputEditor({ value, onChange, disabled, testId }) {
  const rows = value?.rows?.length ? value.rows : [[""]];
  const colCount = rows[0]?.length || 1;

  const setCell = (r, c, text) => {
    const next = rows.map((row) => [...row]);
    next[r][c] = text;
    onChange({ rows: next });
  };
  const addRow = () => onChange({ rows: [...rows, new Array(colCount).fill("")] });
  const removeRow = (r) => {
    if (rows.length <= 1) return;
    onChange({ rows: rows.filter((_, i) => i !== r) });
  };
  const addColumn = () => onChange({ rows: rows.map((row) => [...row, ""]) });
  const removeColumn = () => {
    if (colCount <= 1) return;
    onChange({ rows: rows.map((row) => row.slice(0, -1)) });
  };

  return (
    <div className="space-y-2" data-testid={testId || "table-input-editor"}>
      <div className="overflow-x-auto rounded-lg border border-border">
        <table className="w-full text-sm">
          <tbody>
            {rows.map((row, r) => (
              <tr key={r} className="border-b border-border last:border-0">
                {row.map((cell, c) => (
                  <td key={c} className="border-r border-border last:border-0 p-1">
                    <Input
                      value={cell}
                      disabled={disabled}
                      onChange={(e) => setCell(r, c, e.target.value)}
                      className="border-0 shadow-none h-8 focus-visible:ring-1"
                      data-testid={`table-input-cell-${r}-${c}`}
                    />
                  </td>
                ))}
                <td className="p-1 w-8">
                  <Button type="button" variant="ghost" size="sm" disabled={disabled || rows.length <= 1} onClick={() => removeRow(r)} data-testid={`table-input-remove-row-${r}`}>
                    <Trash2 className="h-3.5 w-3.5 text-muted-foreground" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex items-center gap-2">
        <Button type="button" variant="outline" size="sm" disabled={disabled} onClick={addRow} data-testid="table-input-add-row-btn">
          <Plus className="h-3.5 w-3.5 mr-1" /> Row
        </Button>
        <Button type="button" variant="outline" size="sm" disabled={disabled} onClick={addColumn} data-testid="table-input-add-column-btn">
          <Plus className="h-3.5 w-3.5 mr-1" /> Column
        </Button>
        <Button type="button" variant="outline" size="sm" disabled={disabled || colCount <= 1} onClick={removeColumn} data-testid="table-input-remove-column-btn">
          <Trash2 className="h-3.5 w-3.5 mr-1" /> Column
        </Button>
      </div>
    </div>
  );
}
